import { useCallback, useRef, useState } from 'react'
import { Upload, FileText, X } from 'lucide-react'
import { parseXlsxOnServer, uploadDocument, getDocumentStatus, type ExtractedVehicle, type ParseXlsxStandard } from '../api'
import { useHomeStore } from '../store'
import type { PendingFile } from '../types'

interface Props {
  onExtracted: (carIds: number[]) => void
  onBulkXlsx: (file: File, parsed: ParseXlsxStandard) => void
}

const POLL_INTERVAL_MS = 2000
const POLL_MAX = 60

const XLSX_EXTS = ['.xls', '.xlsx', '.csv']

/** RZ z extrakce → tvar pro pole SPZ; prázdné, "N/A" a podobné zástupné hodnoty zahodí. */
function normalizePlate(raw: unknown): string {
  if (typeof raw !== 'string') return ''
  const plate = raw.replace(/[\s-]/g, '').toUpperCase()
  if (plate.length < 4 || plate.length > 12) return ''
  if (!/^[A-Z0-9]+$/.test(plate)) return ''
  if (/^(NA|NONE|NULL|NEUVEDENO|UNKNOWN|XXX+|0+)$/.test(plate)) return ''
  return plate
}

export function DocumentUploadCard({ onExtracted, onBulkXlsx }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [pending, setPending] = useState<PendingFile[]>([])
  const [dragOver, setDragOver] = useState(false)
  const addVehicles = useHomeStore((s) => s.addVehicles)
  const hasVin = useHomeStore((s) => s.hasVin)

  const onFiles = useCallback(
    async (files: FileList | File[]) => {
      const arr = Array.from(files)
      if (arr.length === 0) return

      // Register every selected file in the list up front so the user sees all
      // of them (and their progress) immediately, instead of one appearing at a
      // time as each finishes processing.
      const entries = arr.map((f) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file: f,
      }))
      setPending((p) => [
        ...p,
        ...entries.map((e) => ({ id: e.id, filename: e.file.name, status: 'uploading' as const })),
      ])

      async function processXlsx(id: string, f: File) {
        try {
          const parsed = await parseXlsxOnServer(f)
          if (parsed.format === 'pro_doma') {
            const newVins = parsed.vins.map((v) => v.trim().toUpperCase()).filter((v) => v && !hasVin(v))
            const ids = addVehicles(newVins.map((vin) => ({ vin })))
            if (ids.length > 0) {
              const store = useHomeStore.getState()
              const docNum = store.allocDocNumber()
              ids.forEach((vid, idx) => store.updateVehicle(vid, { source: `${docNum}.${idx + 1}` }))
            }
            setPending((p) =>
              p.map((x) =>
                x.id === id
                  ? { ...x, status: 'processed', filename: `${f.name} — ${ids.length} VIN přidáno` }
                  : x,
              ),
            )
            if (ids.length > 0) onExtracted(ids)
          } else {
            setPending((p) => p.filter((x) => x.id !== id))
            onBulkXlsx(f, parsed)
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'chyba'
          setPending((p) => p.map((x) => (x.id === id ? { ...x, status: 'error', error: msg } : x)))
        }
      }

      async function processDocument(id: string, f: File) {
        try {
          const res = await uploadDocument(f)
          setPending((p) => p.map((x) => (x.id === id ? { ...x, status: 'queued' } : x)))
          const result = await pollDocument(res.id)
          if (!result) {
            setPending((p) => p.map((x) => (x.id === id ? { ...x, status: 'error', error: 'Timeout extrakce' } : x)))
            return
          }
          const items = result.extracted_metadata?.items ?? []
          const withVin = items
            .map((it) => ({ ...it, vin: (it.vin ?? it.vin_number ?? '').trim().toUpperCase() }))
            .filter((it) => it.vin && !hasVin(it.vin))
          const accepted = uniqueVins(withVin)
          if (accepted.length > 0) {
            const ids = addVehicles(accepted.map((v) => ({ vin: v.vin })))
            const store = useHomeStore.getState()
            const docNum = store.allocDocNumber()
            ids.forEach((vid, idx) => {
              const ev = accepted[idx]
              const patch: Partial<{
                car_gov_data: ExtractedVehicle['car_gov_data']
                registry_not_found: boolean
                specifikace: string
                source_doc_id: number | string
                source: string
                spz: string
              }> = {}
              // RZ přečtená z dokladu (TP, COC) — dřív se zahazovala a pole SPZ zůstalo prázdné
              // (PETR-177). Backend ji už normalizuje (bez mezer a pomlček, velkými písmeny).
              const spz = normalizePlate(ev.registration_plate_number)
              if (spz) patch.spz = spz
              // Link the source document so the "eye" can preview what was read.
              patch.source_doc_id = res.id
              // Control numbering "{document}.{vehicle}" for cross-checking.
              patch.source = `${docNum}.${idx + 1}`
              if (ev.car_gov_data) {
                patch.car_gov_data = ev.car_gov_data
                const data = ev.car_gov_data.Data as Record<string, unknown> | undefined
                if (data) {
                  patch.specifikace =
                    (data.Specifikace as string) ||
                    (data.VozidloDruh2R as string) ||
                    (data.VozidloDruh2 as string) ||
                    (data.DruhVozidla2R as string) ||
                    (data.DruhVozidla2 as string) ||
                    ''
                }
              }
              if (Object.keys(patch).length > 0) store.updateVehicle(vid, patch)
            })
            onExtracted(ids)
          }
          // Doklad bez čitelného VIN, ale s přečtenou SPZ: dřív worker položku zahodil
          // a celý doklad skončil „Chyba". Teď řekneme, co se přečetlo — VIN doplní obsluha.
          const platesWithoutVin = accepted.length
            ? []
            : Array.from(
                new Set(
                  items
                    .filter((it) => !(it.vin ?? it.vin_number ?? '').trim())
                    .map((it) => normalizePlate(it.registration_plate_number))
                    .filter((spz): spz is string => Boolean(spz)),
                ),
              )
          if (platesWithoutVin.length > 0) {
            setPending((p) =>
              p.map((x) =>
                x.id === id
                  ? {
                      ...x,
                      status: 'warning',
                      filename: `${f.name} — VIN nepřečten, SPZ ${platesWithoutVin.join(', ')}; doplňte VIN ručně`,
                    }
                  : x,
              ),
            )
            return
          }
          setPending((p) => p.map((x) => (x.id === id ? { ...x, status: 'processed' } : x)))
        } catch (err) {
          const msg = err instanceof Error ? err.message : 'chyba'
          setPending((p) => p.map((x) => (x.id === id ? { ...x, status: 'error', error: msg } : x)))
        }
      }

      // Process all files concurrently so progress shows for each in parallel.
      await Promise.all(
        entries.map(({ id, file }) => {
          const name = file.name.toLowerCase()
          return XLSX_EXTS.some((e) => name.endsWith(e)) ? processXlsx(id, file) : processDocument(id, file)
        }),
      )
    },
    [addVehicles, hasVin, onBulkXlsx, onExtracted],
  )

  function dismissPending(id: string) {
    setPending((p) => p.filter((x) => x.id !== id))
  }

  return (
    <div>
      <div
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragOver(false)
          if (e.dataTransfer.files) void onFiles(e.dataTransfer.files)
        }}
        onClick={() => inputRef.current?.click()}
        className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${dragOver ? 'border-brand bg-brand-bg' : 'border-zinc-300 hover:border-zinc-400'}`}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx,.doc,.docx,.csv"
          className="hidden"
          onChange={(e) => e.target.files && onFiles(e.target.files)}
        />
        <Upload className="mx-auto mb-4 h-10 w-10 text-zinc-400" />
        <p className="mb-1 font-medium text-zinc-700">Přetáhněte soubory sem nebo klikněte pro procházení</p>
        <p className="text-sm text-zinc-500">Max 50 MB / soubor, celkem max 500 MB.</p>
      </div>

      {pending.length > 0 && (
        <div className="mt-6 space-y-2">
          <h4 className="font-medium text-zinc-900">Soubory k nahrání</h4>
          {pending.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm">
              <FileText className="h-4 w-4 text-zinc-500" />
              <span className="flex-1 truncate">{p.filename}</span>
              <span className={statusClass(p.status)}>{statusLabel(p.status)}</span>
              <button onClick={() => dismissPending(p.id)} className="text-zinc-400 hover:text-zinc-600">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

async function pollDocument(id: number | string) {
  for (let i = 0; i < POLL_MAX; i++) {
    const data = await getDocumentStatus(id)
    if (data.is_processed || data.status === 'processed') return data
    if (data.status === 'failed' || data.status === 'error') throw new Error(data.extracted_metadata?.error || 'Extrakce selhala')
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
  }
  return null
}

function statusClass(s: PendingFile['status']) {
  switch (s) {
    case 'uploading':
      return 'text-zinc-500'
    case 'queued':
      return 'text-amber-600'
    case 'processed':
      return 'text-green-600'
    case 'warning':
      return 'text-amber-700'
    case 'error':
      return 'text-red-600'
  }
}

function statusLabel(s: PendingFile['status']) {
  switch (s) {
    case 'uploading':
      return 'Nahrávám…'
    case 'queued':
      return 'Extrahuji…'
    case 'processed':
      return 'Hotovo'
    case 'warning':
      return 'Bez VIN'
    case 'error':
      return 'Chyba'
  }
}

function uniqueVins<T extends { vin: string }>(items: T[]): T[] {
  const seen = new Set<string>()
  const out: T[] = []
  for (const it of items) {
    if (!it.vin || seen.has(it.vin)) continue
    seen.add(it.vin)
    out.push(it)
  }
  return out
}
